import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { AuthModal } from '../auth/AuthModal';
import { initialFor, resolveDisplayName } from '../auth/displayName';

interface SettingsEntry {
  label: string;
  hint: string;
  icon: React.ReactElement;
  to: string;
}

/**
 * The one control in the corner: who you are, everything account-shaped, and the
 * way out.
 *
 * This replaced a separate account button sitting beside a settings cog. Two
 * controls for "your side of the app" invited the question of which one held
 * what, and the answer was arbitrary. A single user icon is the convention
 * people already expect, and it gives the menu an obvious identity.
 *
 * Shown signed-out too. The destinations render their own signed-out state and
 * offer the way in, which matches the rest of the app: browsing is open, and the
 * account is asked for at the point it is actually needed.
 */
const ENTRIES: SettingsEntry[] = [
  {
    label: 'Credit and Billing',
    hint: 'Balance, top-ups and usage',
    icon: <AccountBalanceWalletOutlinedIcon fontSize="small" />,
    to: '/settings/billing',
  },
  {
    label: 'API keys',
    hint: 'Use your own provider keys',
    icon: <KeyOutlinedIcon fontSize="small" />,
    to: '/settings/keys',
  },
  {
    label: 'Asset gallery',
    hint: 'Everything you have made',
    icon: <CollectionsOutlinedIcon fontSize="small" />,
    to: '/assets',
  },
];

export const SettingsMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const open = Boolean(anchorEl);

  const go = (to: string) => {
    setAnchorEl(null);
    navigate(to);
  };

  const displayName = resolveDisplayName(user);

  return (
    <>
      <Tooltip title={isAuthenticated ? displayName : 'Account'}>
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          aria-label="Account and settings"
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          sx={{ color: 'text.secondary' }}
        >
          {isAuthenticated ? (
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 14 }}>
              {initialFor(user)}
            </Avatar>
          ) : (
            <AccountCircleOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, borderRadius: 2, mt: 0.5 } } }}
      >
        {/* Flat list of MenuItems, no dividers or wrapper nodes: MenuList walks
            its children to move focus with the arrow keys, and anything that
            isn't a menu item in that list is a hole to navigate around. That is
            why the identity row is itself a menu item rather than a header —
            it shows who you are AND takes you to the account page. */}
        {isAuthenticated && (
          <MenuItem onClick={() => go('/settings/account')} sx={{ py: 1 }}>
            <ListItemIcon>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                {initialFor(user)}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={displayName}
              secondary={user?.email}
              primaryTypographyProps={{ fontWeight: 700, variant: 'body2', noWrap: true }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </MenuItem>
        )}

        {ENTRIES.map(entry => (
          <MenuItem key={entry.to} onClick={() => go(entry.to)} sx={{ py: 1 }}>
            <ListItemIcon sx={{ color: 'text.secondary' }}>{entry.icon}</ListItemIcon>
            <ListItemText
              primary={entry.label}
              secondary={entry.hint}
              primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}

        {/* The only way in or out now that the separate account button is gone. */}
        {isAuthenticated ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setConfirmSignOut(true);
            }}
            sx={{ py: 1 }}
          >
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Sign out"
              primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
            />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setAuthModalOpen(true);
            }}
            sx={{ py: 1 }}
          >
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <LoginOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Sign in"
              secondary="Or create an account"
              primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Confirmed because signing out clears the query cache and the agent bar's
          saved conversations — recoverable, but not obviously so at the moment
          someone clicks the wrong row of a menu. */}
      <Dialog open={confirmSignOut} onClose={() => setConfirmSignOut(false)}>
        <DialogTitle>Sign out?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your work stays saved to your account. You&apos;ll need to sign in again to reach it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSignOut(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmSignOut(false);
              logout();
            }}
            color="error"
            variant="contained"
          >
            Sign out
          </Button>
        </DialogActions>
      </Dialog>

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};
